/* eslint-disable prefer-destructuring */
import _ from 'underscore';
import { useCallback, useContext, useMemo, useState } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { handleBulkErrors } from '@bingads-webui-campaign-react/pmax-common';
import {
  ENTITY_ACTION_TYPES,
  targetOptions,
  LocationTargetTypes,
  getIntentValue,
  getIntentCriterionValues,
  getIntentCriterion,
  getLocationsToSave,
  generateIntentTargetValues,
  fromLocationCriterionToBingMapTarget,
  fromBingMapTargetToLocationTargetEDM
} from '@bingads-webui-campaign-react/location-targeting';
import { readLocationCriterions, createOrUpdateCampaign } from '@bingads-webui-campaign-react/perf-max-data';

export function useTargetAdapter(logger) {
  const { scope } = useContext(ScopeContext);
  const { cid, aid, campaignId } = scope.values;

  const i18n = useContext(I18nContext);

  const [isLoading, setIsLoading] = useState(false);

  // Maintain internal states to do comparison
  const [internalLocationTargets, setInternalLocationTargets] = useState([]);
  const [internalNegativeLocationTargets, setInternalNegativeLocationTargets] = useState([]);

  const [internalIntentCriterion, setInternalIntentCriterion] = useState(null);
  const [internalPeopleInLocation, setInternalPeopleInLocation] = useState(null);
  const [internalPeopleSearchingForLocation, setInternalPeopleSearchingForLocation] = useState(null);

  const internalPeopleInOrSearchingForLocation = internalPeopleInLocation && internalPeopleSearchingForLocation;

  const readTargets = useCallback(() => {
    setIsLoading(true);

    logger.trace('Start reading location criterions');
    return readLocationCriterions(cid, aid, campaignId).then((res) => {
      const criterions = res.value;
      logger.trace(`read location criterions: ${JSON.stringify(criterions)}`);

      if (_.isEmpty(criterions)) {
        return {};
      }

      const initialTargets = fromLocationCriterionToBingMapTarget(criterions);
      const intentCriterion = getIntentCriterion(criterions);

      const initialLocationTargets = _.filter(
        initialTargets,
        target => target.targetingType === LocationTargetTypes.Included
      );
      const initialNegativeLocationTargets = _.filter(
        initialTargets,
        target => target.targetingType === LocationTargetTypes.Excluded
      );

      const { peopleInLocation, peopleSearchingForLocation } = getIntentCriterionValues(intentCriterion);

      setInternalLocationTargets(initialLocationTargets);
      setInternalNegativeLocationTargets(initialNegativeLocationTargets);

      setInternalPeopleInLocation(peopleInLocation);
      setInternalPeopleSearchingForLocation(peopleSearchingForLocation);
      setInternalIntentCriterion(intentCriterion);

      const selectedLocationOption = (!_.isEmpty(initialLocationTargets) || !_.isEmpty(initialNegativeLocationTargets))
        ? targetOptions.specific
        : targetOptions.everywhere;

      return {
        positiveLocationTargets: initialLocationTargets,
        negativeLocationTargets: initialNegativeLocationTargets,
        isPeopleInLocationChecked: peopleInLocation,
        isPeopleSearchingForLocationChecked: peopleSearchingForLocation,
        selectedLocationOption,
      };
    }).catch((e) => {
      logger.error(`read location failed, body: ${campaignId}, error: ${JSON.stringify(e)}`);
      throw new Error(e);
    }).finally(() => {
      setIsLoading(false);
      logger.trace('Finish reading location criterions');
    });
  }, [aid, campaignId, cid, logger]);

  // Calculate the difference between currentTargets and initialTargets and create/delete as needed
  const writeLocationTargetChanges = useCallback(({
    positiveLocationTargets,
    negativeLocationTargets,
    selectedLocationOption,
    isPeopleInLocationChecked,
    isPeopleSearchingForLocationChecked,
  }) => {
    let locationsToAdd = [];
    let locationsToDelete = [];

    const currentLocationTargets = _.union(positiveLocationTargets, negativeLocationTargets);
    const initialLocationTargets = _.union(internalLocationTargets, internalNegativeLocationTargets);

    if (selectedLocationOption === targetOptions.everywhere) {
      // if everywhere is selected, set all locations for delete
      locationsToDelete = initialLocationTargets;
    } else {
      const locationsToSave = getLocationsToSave(initialLocationTargets, currentLocationTargets);
      locationsToAdd = locationsToSave.locationsToAdd;
      locationsToDelete = locationsToSave.locationsToDelete;
    }

    locationsToAdd = fromBingMapTargetToLocationTargetEDM(
      locationsToAdd,
      campaignId,
      ENTITY_ACTION_TYPES.Add
    );
    locationsToDelete = fromBingMapTargetToLocationTargetEDM(
      locationsToDelete,
      campaignId,
      ENTITY_ACTION_TYPES.Delete
    );

    locationsToDelete = _.map(
      locationsToDelete,
      deletedValue => _.extend(deletedValue, { Status: 'Deleted' })
    );

    const internalIntent = getIntentValue({
      peopleInLocation: internalPeopleInLocation,
      peopleSearchingForLocation: internalPeopleSearchingForLocation,
      peopleInOrSearchingForLocation: internalPeopleInOrSearchingForLocation,
    });

    const newIntent = getIntentValue({
      peopleInLocation: isPeopleInLocationChecked,
      peopleSearchingForLocation: isPeopleSearchingForLocationChecked,
      peopleInOrSearchingForLocation: isPeopleSearchingForLocationChecked && isPeopleInLocationChecked,
    });

    const isIntentValueChanged = internalIntent !== newIntent;

    const intentTargets = generateIntentTargetValues(newIntent, internalIntentCriterion, campaignId);

    if (_.isEmpty(locationsToAdd) && _.isEmpty(locationsToDelete) && !isIntentValueChanged) {
      return Promise.resolve();
    }

    const modelToSave = {
      Id: campaignId,
      CampaignCriterions: _.union(
        locationsToAdd,
        locationsToDelete,
        intentTargets
      ),
    };

    logger.trace(`Start writing location targets: ${JSON.stringify(modelToSave)}`);

    return createOrUpdateCampaign(modelToSave).then(() => {
      logger.trace(`Write location targets successfully: ${JSON.stringify(modelToSave)}`);
    }).catch((error) => {
      logger.error(`Write location targets failed, body: ${JSON.stringify(modelToSave)}, error: ${JSON.stringify(error)}`);

      handleBulkErrors(error, i18n);

      return Promise.reject(error);
    }).finally(() => {
      setIsLoading(false);
      logger.trace('Finish writing location targets');
    });
  }, [
    campaignId, internalLocationTargets, internalNegativeLocationTargets,
    internalIntentCriterion, internalPeopleInLocation, internalPeopleInOrSearchingForLocation,
    internalPeopleSearchingForLocation, i18n, logger,
  ]);

  const entityState = useMemo(() => ({
    isLoading,
  }), [isLoading]);

  return [entityState, {
    readTargets,
    writeLocationTargetChanges,
  }];
}
